<script context="module">
    import { getNumber, getText } from "../../../Utils/utilFunctions.svelte";

    export const getDataByPeriod = (
        dataObj,
        interval,
        dateFrom,
        dateTo,
        topIndicatorName,
    ) => {
        let chartData = [];
        let labelArr = [];

        if (!dataObj) return { chartData, labelArr };

        const getFormatDate = (date) => {
            const d = new Date(date);
            return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        };

        const _processEntry = (entry, label) => {
            const labels = [];
            const data = [];

            for (const [key, value] of entry) {
                labels.push(getText(key));
                data.push(getNumber(value));
            }

            chartData.push({ label: [...labels], data: [...data] });
            labelArr.push(label);

            !interval && _fillMissingDates(chartData);
        };

        const _fillMissingDates = (arr) => {
            const filledArr = [];

            arr.forEach((obj) => {
                const labels = obj.label;
                const data = obj.data;

                const filledLabels = [];
                const filledData = [];

                const firstDate = new Date(dateFrom);
                const lastDate = new Date(dateTo).setDate(
                    new Date(dateTo).getDate() + 1,
                );

                let currentDate = new Date(firstDate);

                while (currentDate < lastDate) {
                    const formattedDate = currentDate
                        .toISOString()
                        .split("T")[0];
                    const dataIndex = labels.findIndex(
                        (date) =>
                            getFormatDate(date) == getFormatDate(formattedDate),
                    );

                    if (dataIndex !== -1) filledData.push(data[dataIndex]);
                    else filledData.push("0");

                    filledLabels.push(getFormatDate(formattedDate));

                    currentDate.setDate(currentDate.getDate() + 1);
                }

                filledArr.push({
                    label: filledLabels,
                    data: filledData,
                });
            });

            chartData = filledArr;
        };

        const processDaily = () => {
            const entries = Object.entries(dataObj);
            _processEntry(entries, topIndicatorName);
        };

        const processWeekly = () => {
            const weeklyData = {};

            // Convert start and end dates to Date objects
            const startDate = new Date(`${dateFrom}T00:00:00`);
            const endDate = new Date(`${dateTo}T23:59:59`);

            // Filter data entries within the date range and sort by date
            const dataEntries = Object.entries(dataObj)
                .map(([dateStr, value]) => ({ date: new Date(dateStr), value }))
                .filter(({ date }) => date >= startDate && date <= endDate)
                .sort((a, b) => a.date - b.date);

            // Process each entry to group by week
            dataEntries.forEach(({ date, value }) => {
                const currentDate = new Date(date);
                const weekStart = new Date(currentDate);
                weekStart.setDate(
                    currentDate.getDate() - currentDate.getDay() + 1,
                ); // Start from Monday
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6); // End on Sunday

                const newKey = `${getFormatDate(weekStart)} - ${getFormatDate(weekEnd)}`;
                if (!weeklyData[newKey]) weeklyData[newKey] = 0;
                weeklyData[newKey] += value;
            });

            // Fill in missing weeks with zero values
            let currentWeek = new Date(startDate);
            while (currentWeek <= endDate) {
                const weekStart = new Date(currentWeek);
                weekStart.setDate(
                    currentWeek.getDate() - currentWeek.getDay() + 1,
                ); // Start from Monday
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6); // End on Sunday

                const newKey = `${getFormatDate(weekStart)} - ${getFormatDate(weekEnd)}`;
                if (!weeklyData[newKey]) weeklyData[newKey] = 0;

                // Move to the next week
                currentWeek.setDate(currentWeek.getDate() + 7);
            }

            // Adjust the first entry if it overlaps with the start date
            const resultEntries = Object.entries(weeklyData);

            // Sort resultEntries by date
            resultEntries.sort(
                (a, b) =>
                    new Date(
                        a[0].split(" - ")[0].split("/").reverse().join("-"),
                    ) -
                    new Date(
                        b[0].split(" - ")[0].split("/").reverse().join("-"),
                    ),
            );

            if (resultEntries.length > 0) {
                const [firstLabel, firstValue] = resultEntries[0];
                const [firstStart, firstEnd] = firstLabel
                    .split(" - ")
                    .map(
                        (dateStr) =>
                            new Date(dateStr.split("/").reverse().join("-")),
                    );

                if (firstStart < startDate) {
                    const adjustedKey = `${getFormatDate(startDate)} - ${getFormatDate(firstEnd)}`;
                    weeklyData[adjustedKey] = weeklyData[firstLabel];
                    delete weeklyData[firstLabel];
                    resultEntries[0][0] = adjustedKey;
                }
            }

            // Adjust the last entry if it overlaps with the end date
            if (resultEntries.length > 0) {
                const lastIndex = resultEntries.length - 1;
                const [lastLabel, lastValue] = resultEntries[lastIndex];
                const [lastStart, lastEnd] = lastLabel
                    .split(" - ")
                    .map(
                        (dateStr) =>
                            new Date(dateStr.split("/").reverse().join("-")),
                    );
                if (lastEnd > endDate) {
                    const adjustedKey = `${getFormatDate(lastStart)} - ${getFormatDate(endDate)}`;
                    weeklyData[adjustedKey] = weeklyData[lastLabel];
                    delete weeklyData[lastLabel];
                    resultEntries[lastIndex][0] = adjustedKey;
                }
            }

            // Convert resultEntries back to an object
            const sortedWeeklyData = Object.fromEntries(resultEntries);

            // Process the sorted data
            _processEntry(Object.entries(sortedWeeklyData), topIndicatorName);
        };

        const processMonthly = () => {
            const monthlyData = {};

            const startDate = new Date(`${dateFrom}T00:00:00`);
            const endDate = new Date(`${dateTo}T23:59:59`);

            const monthNames = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
            ];

            const getMonth = (monthIndex) => monthNames[monthIndex];

            // Filter and process data entries
            for (const [key, value] of Object.entries(dataObj)) {
                const date = new Date(key);

                // Skip dates outside the specified range
                if (date < startDate || date > endDate) {
                    continue;
                }

                const dataYear = date.getFullYear();
                const dataMonth = getMonth(date.getMonth());
                const newKey = `${dataMonth}-${dataYear}`;

                if (!monthlyData[newKey]) {
                    monthlyData[newKey] = 0;
                }

                monthlyData[newKey] += value;
            }

            // Fill in missing months with zero values
            let currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                const dataYear = currentDate.getFullYear();
                const dataMonth = getMonth(currentDate.getMonth());
                const newKey = `${dataMonth}-${dataYear}`;

                if (!monthlyData[newKey]) {
                    monthlyData[newKey] = 0;
                }

                // Move to the next month
                currentDate.setMonth(currentDate.getMonth() + 1);
            }

            // Sort the monthlyData by date keys
            const sortedMonthlyData = Object.keys(monthlyData)
                .sort((a, b) => {
                    const [monthA, yearA] = a.split("-");
                    const [monthB, yearB] = b.split("-");
                    const dateA = new Date(
                        `${yearA}-${monthNames.indexOf(monthA) + 1}-01`,
                    );
                    const dateB = new Date(
                        `${yearB}-${monthNames.indexOf(monthB) + 1}-01`,
                    );
                    return dateA - dateB;
                })
                .reduce((acc, key) => {
                    acc[key] = monthlyData[key];
                    return acc;
                }, {});

            _processEntry(Object.entries(sortedMonthlyData), topIndicatorName);
        };

        switch (interval) {
            case 0:
                processDaily();
                break;
            case 1:
                processWeekly();
                break;
            case 2:
                processMonthly();
                break;
            default:
                throw new Error("Invalid interval specified");
        }

        return { chartData, labelArr };
    };

    export const getDoctorMonthlyInvoicesLineChartData = (data, filters) => {
        const { dateFrom, dateTo } = filters;

        const keys = [],
            values = [];

        const startDate = new Date(dateFrom),
            endDate = new Date(dateTo);

        for (const [key, value] of Object.entries(data)) {
            const keyDate = new Date(key);

            if (keyDate >= startDate && keyDate <= endDate) {
                keys.push({ data: key });
                values.push(value);
            }

            // keys.push({ data: key });
            // values.push(value);
        }

        return { keys, values };
    };
</script>
